import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import './RtoSummary.css'

/**
 * What the RTO holds about this vehicle, on the vehicle's own page.
 *
 * The sweep bought a response for 609 of 611 vehicles and most of what it held
 * was reachable only from the register — you had to go back out to the list to
 * see the owner, the RTO office or who financed a vehicle you already had open.
 *
 * This is the summary, not the record: the fields somebody reading a vehicle
 * actually asks for, and a way through to the rest. The full record — provenance,
 * the unmapped fields, the archived payload — stays one page away, because it is
 * for checking a mapping rather than for reading a vehicle.
 *
 * Keyed on `['registry-snapshot', vehicleId]`, the SAME key the RTO record page
 * uses, so opening the full record after reading this costs no second request.
 */
export function RtoSummary({
  vehicleId,
  registration,
}: {
  vehicleId: number
  registration: string
}) {
  const snapshot = useQuery({
    queryKey: ['registry-snapshot', vehicleId],
    queryFn: () => api.getRegistrySnapshot(vehicleId),
  })

  if (snapshot.isPending) {
    return (
      <section className="rtos">
        <h4>What the RTO holds</h4>
        <p className="rtos__none">Loading…</p>
      </section>
    )
  }

  const s = snapshot.data

  /*
    Never fetched is not the same as the registry having nothing to say, and only
    one of the two is worth acting on. An empty panel would state the second.
  */
  if (!s) {
    return (
      <section className="rtos">
        <h4>What the RTO holds</h4>
        <p className="rtos__none">
          Nothing held — this vehicle has never been fetched from the registry.
        </p>
      </section>
    )
  }

  const rcAlert = Boolean(s.rcStatus && s.rcStatus.toUpperCase() !== 'ACTIVE')

  return (
    <section className="rtos">
      <h4>What the RTO holds</h4>

      <dl className="rtos__facts">
        <div>
          <dt>RC status</dt>
          <dd className={rcAlert ? 'rtos__alert' : undefined}>{s.rcStatus || '—'}</dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{s.ownerName || '—'}</dd>
        </div>
        <div>
          <dt>RTO office</dt>
          <dd>{s.rtoName || '—'}</dd>
        </div>
        <div>
          <dt>Class</dt>
          <dd>{s.vehicleClass || '—'}</dd>
        </div>
        <div>
          <dt>Fuel</dt>
          <dd>{s.fuelType || '—'}</dd>
        </div>
        <div>
          <dt>Emission norms</dt>
          <dd>{s.emissionNorms || '—'}</dd>
        </div>
        <div className="rtos__wide">
          <dt>Financed</dt>
          <dd>
            {s.isFinanced === true
              ? s.financer || 'Yes'
              : s.isFinanced === false
                ? 'No'
                : // Three states, not two: the registry never said.
                  '—'}
          </dd>
        </div>
      </dl>

      <Link className="btn btn--rto" to={`/vehicles/${registration}/rto`}>
        See the full RTO record ›
      </Link>
    </section>
  )
}
